import { Button, Tooltip, Chip, TextField, Box, MenuItem } from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
} from "@mui/x-data-grid";
import axios from "axios";
import { useEffect, useState } from "react";
import DetailsIcon from "@mui/icons-material/Details";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import dayjs from "dayjs";
import BreadcrumbsNav from "./BreadcrumbsNav";

const Facturas = () => {
  const [data, setData] = useState([]);
  const [selectedFacturas, setSelectedFacturas] = useState([]);
  const [envios, setEnvios] = useState([]);
  const [selectedEnvio, setSelectedEnvio] = useState(null);
  const [openEnvioModal, setOpenEnvioModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const [proveedorFiltro, setProveedorFiltro] = useState("");
  const navigate = useNavigate();
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    id: false,
    descripcion: true,
    estatus: true,
    serie: false
  });

  const apiUrl =
    process.env.NODE_ENV === "production"
      ? process.env.REACT_APP_API_URL
      : process.env.REACT_APP_API_URL_LOCAL;

  const dataFiltrada = data.filter((factura) => {
    const texto = busqueda.toLowerCase().trim();

    const coincideTexto =
      !texto ||
      String(factura.folio || "")
        .toLowerCase()
        .includes(texto) ||
      String(factura.numero_factura || "")
        .toLowerCase()
        .includes(texto) ||
      String(factura.envio_id || "")
        .toLowerCase()
        .includes(texto);

    const coincideProveedor =
      !proveedorFiltro || factura.proveedor_nombre === proveedorFiltro;

    return coincideTexto && coincideProveedor;
  });

  const CustomToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport
        csvOptions={{ fileName: "exported_data", utf8WithBom: true }}
      />
    </GridToolbarContainer>
  );

  useEffect(() => {
    fetchFacturas();
  }, [apiUrl]);

  const fetchFacturas = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/facturas/`);
      if (
        response.data.data &&
        Array.isArray(response.data.data) &&
        response.data.data.length > 0
      ) {
        setData(response.data.data);
        //setFilteredEnvios(response.data.data);
      }
    } catch (error) {
      setData([]);
      const errorMessage =
        error.response?.data?.message || "Error al cargar los datos";
      Swal.fire({
        title: "Error",
        text: errorMessage,
        icon: "warning",
        timer: 5000,
        showCloseButton: true,
        allowEscapeKey: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchEnviosAbiertos = async () => {
    try {
      const response = await axios.get(`${apiUrl}/empaque/fetchEnviosAbiertos`);
      setEnvios(response.data.data || []);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Error al cargar los datos";
      Swal.fire({
        title: "Error",
        text: errorMessage,
        icon: "warning",
        timer: 5000,
        showCloseButton: true,
        allowEscapeKey: true,
      });
    }
  };

  const asignarFacturas = async () => {
    try {
      await axios.post(`${apiUrl}/facturas/asignarFacturasAEnvio`, {
        envio_id: selectedEnvio,
        factura_ids: selectedFacturas,
      });

      Swal.fire("Éxito", "Facturas asignadas correctamente", "success");
      handleCloseEnvioModal();
      setSelectedFacturas([]);
      fetchFacturas();
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Error al asignar facturas",
        "error",
      );
    }
  };

  const handleCloseEnvioModal = () => {
    setOpenEnvioModal(false);
    setSelectedEnvio(null);
  };

  const handleDetallesFactura = (facturaId, proveedorNombre) => {
    navigate(`/detalleFacturas/factura/${facturaId}`, {
      state: { proveedorNombre }, // así lo mandas al otro componente
    });
  };

  const columns = [
    {
      field: "id",
      headerName: "Folio interno",
      type: "text",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "fecha_arribo",
      headerName: "Fecha",
      type: "text",
      flex: 2,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "proveedor_nombre",
      headerName: "Proveedor",
      type: "text",
      flex: 2,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "serie",
      headerName: "Serie",
      type: "text",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "folio",
      headerName: "Folio",
      type: "text",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "total",
      headerName: "Total",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "proforma",
      headerName: "Proforma",
      type: "text",
      flex: 1.5,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "envio_id",
      headerName: "# Envío",
      flex: 0.9,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        return params.value ? (
          <Chip label={`#${params.value}`} size="small" variant="outlined" />
        ) : (
          <Chip label="Sin envío" size="small" variant="outlined" />
        );
      },
    },
    {
      field: "estatus",
      headerName: "Estatus",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const v = String(params.value || "").toLowerCase();

        const map = {
          recibido: { label: "Recibido", color: "success" },
          pendiente: { label: "Pendiente", color: "warning" },
          asignado: { label: "Asignado", color: "info" },
          cancelado: { label: "Cancelado", color: "error" },
        };

        const cfg = map[v] || { label: params.value || "—", color: "default" };

        return <Chip label={cfg.label} color={cfg.color} size="small" />;
      },
    },
    {
      field: "actions",
      headerName: "Acciones",
      type: "actions",
      getActions: (params) => [
        <Tooltip title="Detalles" key={`envios-${params.row.id}`}>
          <GridActionsCellItem
            icon={<DetailsIcon />}
            sx={{ color: "blue" }}
            label="Detalles"
            onClick={() =>
              handleDetallesFactura(params.row.id, params.row.proveedor_nombre)
            }
          />
        </Tooltip>,
      ],
    },
  ];

  const columnsEnvios = [
    { field: "id", headerName: "ID", flex: 1 },
    { field: "folio_interno", headerName: "Folio Interno", flex: 1 },
    { field: "descripcion", headerName: "Descripción", flex: 2 },
    {
      field: "fecha_creacion",
      headerName: "Fecha de Creación",
      flex: 1.5,
      valueFormatter: (params) => dayjs(params.value).format("DD/MM/YYYY"),
    },
    {
      field: "fecha_programada",
      headerName: "Fecha Programada",
      flex: 1.5,
      valueFormatter: (params) => dayjs(params.value).format("DD/MM/YYYY"),
    },
    { field: "estatus", headerName: "Estatus", flex: 1 },
  ];

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        fontFamily: "Montserrat",
        fontWeight: "bold",
      }}
    >
      <div
        style={{
          flexDirection: "row",
          fontFamily: "Montserrat",
          textAlign: "center",
          width: "90%",
          marginTop: "10px",
        }}
      >
        <h1>Facturas</h1>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: "-20px",
          }}
        >
          <BreadcrumbsNav />
          <Button
            variant="contained"
            color="primary"
            sx={{
              mb: 2,
            }}
            disabled={selectedFacturas.length === 0}
            onClick={() => {
              //console.log("Facturas seleccionadas:", selectedFacturas);
              setOpenEnvioModal(true);
              fetchEnviosAbiertos();
            }}
          >
            Asignar facturas a un envío ({selectedFacturas.length})
          </Button>
        </div>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 2,
            alignItems: "center",
          }}
        >
          <TextField
            label="Buscar factura o envío"
            size="small"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Número de factura o envío..."
            sx={{ width: 320 }}
          />

          <TextField
            select
            label="Proveedor"
            size="small"
            value={proveedorFiltro}
            onChange={(e) => setProveedorFiltro(e.target.value)}
            sx={{ width: 360 }}
          >
            <MenuItem value="">Todos</MenuItem>

            {[
              ...new Set(
                data.map((item) => item.proveedor_nombre).filter(Boolean),
              ),
            ]
              .sort()
              .map((proveedor) => (
                <MenuItem key={proveedor} value={proveedor}>
                  {proveedor}
                </MenuItem>
              ))}
          </TextField>
        </Box>
        <DataGrid
          sx={{
            height: "65vh",
            borderRadius: 4,
            boxShadow: 24,
            borderWidth: 3,
            borderColor: "#1e88e5",
          }}
          rows={dataFiltrada}
          columns={columns}
          loading={loading}
          showCellVerticalBorder
          showColumnVerticalBorder
          getRowId={(row) => row.id}
          checkboxSelection
          disableRowSelectionOnClick
          isRowSelectable={(params) =>
            params.row.estatus === "recibido" && params.row.envio_id == null
          }
          onRowSelectionModelChange={(newSelection) => {
            setSelectedFacturas(newSelection);
          }}
          rowSelectionModel={selectedFacturas}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(newModel) =>
            setColumnVisibilityModel(newModel)
          }
          experimentalFeatures={{ newEditingApi: true }}
          density="compact" // Establece el tamaño de las filas en compacto por defecto
          slots={{ toolbar: CustomToolbar }}
        />
        <Dialog
          open={openEnvioModal}
          onClose={handleCloseEnvioModal}
          fullWidth
          maxWidth="lg"
          PaperProps={{
            sx: {
              width: '95%',
              maxWidth: 1200,
              borderRadius: 3,
            },
          }}
        >
          <DialogTitle
            sx={{
              fontWeight: 700,
              pb: 1,
            }}
          >
            Selecciona un envío para asignar las facturas seleccionadas
          </DialogTitle>

          <DialogContent
            sx={{
              height: 550,
              pt: 2,
              px: 3,
            }}
          >
            <DataGrid
              sx={{
                height: '100%',
                width: '100%',
              }}
              rows={envios}
              columns={columnsEnvios}
              getRowId={(row) => row.id}
              checkboxSelection
              disableRowSelectionOnClick
              onRowSelectionModelChange={(selection) => {
                setSelectedEnvio(selection[0] || null);
              }}
              rowSelectionModel={
                selectedEnvio
                  ? [selectedEnvio]
                  : []
              }
            />
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              py: 2,
              borderTop: '1px solid #e0e0e0',
            }}
          >
            <Button
              onClick={handleCloseEnvioModal}
              sx={{
                textTransform: 'none',
              }}
            >
              Cancelar
            </Button>

            <Button
              variant="contained"
              disabled={!selectedEnvio}
              onClick={asignarFacturas}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Confirmar asignación
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default Facturas;
